import React, { useEffect, useState } from 'react';
import { 
    Box, 
    Card, 
    CardHeader, 
    CardBody, 
    Heading, 
    Spinner, 
    Center, 
    Alert, 
    AlertIcon, 
    VStack, 
    Text 
} from '@chakra-ui/react';
import { useParams } from 'react-router-dom';
import { useNavigateToList } from '@/hooks/useNavigateToList';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { TruckMaintenanceForm } from '@/components/forms/TruckMaintenanceForm';
import { apiClient } from '@/lib/api/client';
import { useToastNotification } from '@/lib/utils/useToastNotification';
export default function EditMaintenancePage() {
    const { showError } = useToastNotification();
    const { id: idParam } = useParams<{ truckId: string; id: string }>();

    const [maintenance, setMaintenance] = useState<any>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [debugError, setDebugError] = useState<string | null>(null);

    const rawMaintenanceId = idParam;
    const maintenanceId = rawMaintenanceId ? parseInt(String(rawMaintenanceId), 10) : 0;

    useEffect(() => {
        if (!maintenanceId || isNaN(maintenanceId)) {
            setDebugError("Invalid Maintenance ID in URL.");
            setIsLoading(false);
            return;
        }

        const fetchData = async () => {
            try {
                const res = await apiClient.get(`/trucks/truck-maintenance/${maintenanceId}`);
                const data = res.data.data || res.data;

                if (data.maintenance_date) {
                    data.maintenance_date = data.maintenance_date.split('T')[0];
                }
                
                setMaintenance(data);
            } catch (err: any) {
                setDebugError(err.message || "Failed to fetch data");
                showError(err);
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [maintenanceId, showError]);

    const historyFallback = (() => {
        const tId = maintenance?.truck_id;
        if (!tId) return '/fleet-management/trucks';
        const q = new URLSearchParams();
        const plate = maintenance?.fleet_truck_plate_number;
        if (plate != null && plate !== '') q.set('plate_number', String(plate));
        const qs = q.toString();
        return `/fleet-management/maintenance-history/${tId}${qs ? `?${qs}` : ''}`;
    })();
    const navigateToList = useNavigateToList(historyFallback);

    const handleBackToHistory = () => {
        navigateToList();
    };

    if (isLoading) {
        return (
            <AdminLayout>
                <Center h="full">
                    <VStack spacing={4}>
                        <Spinner size="xl" color="blue.500" thickness="4px" />
                        <Text fontWeight="medium">Fetching record details...</Text>
                    </VStack>
                </Center>
            </AdminLayout>
        );
    }

    return (
        <AdminLayout>
            {/* 1. Container: Forced full height, hidden overflow, flex column */}
            <Box p={4} h="full" overflow="hidden" display="flex" flexDirection="column">
                
                {/* 2. Card: Set to h="full" and flex column to partition header and body */}
                <Card h="full" display="flex" flexDirection="column" variant="outline" boxShadow="sm">
                    
                    {/* 3. Header: Fixed with flexShrink 0 */}
                    <CardHeader py={4} borderBottom="1px" borderColor="gray.100" flexShrink={0}> 
                        <Heading size="md">Edit Record: {maintenance?.receipt_number}</Heading>
                        <Text fontSize="sm" color="gray.500">
                            Plate: {maintenance?.fleet_truck_plate_number}
                        </Text>
                    </CardHeader>
                    
                    {/* 4. Body: Only this section scrolls (overflowY="auto") */}
                    <CardBody py={6} overflowY="auto" flex="1" minH={0}>
                        {debugError || !maintenance ? (
                            <Alert status="error" borderRadius="md">
                                <AlertIcon />
                                {debugError || "Maintenance record not found."}
                            </Alert>
                        ) : (
                            <TruckMaintenanceForm 
                                key={maintenance.id} // Forces re-init when data loads
                                truckId={maintenance.truck_id}
                                maintenanceId={maintenance.id}
                                plateNumber={maintenance.fleet_truck_plate_number}
                                initialData={maintenance} 
                                isEdit={true}
                                onSuccess={handleBackToHistory}
                                onCancel={() => navigateToList()}
                            />
                        )}
                    </CardBody>
                </Card>
            </Box>
        </AdminLayout>
    );
}
